import React, { memo, useEffect, useMemo } from "react";
import { Handle, Position, useStore, useUpdateNodeInternals } from "@xyflow/react";
import { ArrowRight, CircleDot, Flag, GitBranch, MapPin, MessageSquareText, Users } from "lucide-react";
import { createStoryAnswerHandleId, STORY_TRACK_TYPES } from "./storyModel";

function StoryPlotNode({ id, data, selected, isConnectable }) {
  const zoom = useStore((state) => state.transform[2]);
  const updateNodeInternals = useUpdateNodeInternals();
  const isEnding = data.nodeType === "ending";
  const answers = Array.isArray(data.answers) ? data.answers : [];
  const unlinkedCount = answers.filter((answer) => !answer.targetNodeId).length;
  const zoomTier = zoom < 0.48 ? "simple" : zoom < 0.78 ? "compact" : "full";
  // Questions and answers are the graph itself, so never hide them behind a zoom tier.
  const showAnswerDetails = !isEnding;
  const track = STORY_TRACK_TYPES[data.trackType] || STORY_TRACK_TYPES.main;
  const answerHandleKey = useMemo(
    () => answers.map((answer) => `${answer.id}:${answer.targetNodeId || ""}`).join("|"),
    [answers],
  );

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id));
    return () => window.cancelAnimationFrame(frame);
  }, [answerHandleKey, id, showAnswerDetails, updateNodeInternals]);

  return (
    <article
      className={`interactive-plot-node story-zoom-${zoomTier} story-track-${track.value} ${selected ? "is-selected" : ""} ${isEnding ? "is-ending" : ""}`}
      style={{ "--story-track-color": track.color }}
      aria-label={`${track.label}，${isEnding ? "结局" : "剧情"}节点：${data.title || "未命名"}`}
    >
      <Handle id="target" type="target" position={Position.Top} className="interactive-node-handle interactive-target-handle" isConnectable={isConnectable} />

      <div className="interactive-plot-node__card">
        <header className="interactive-plot-node__header">
          <div className="interactive-plot-node__eyebrow">
            <span className="interactive-node-track"><i aria-hidden="true" />{track.shortLabel}</span>
            {data.isEntry ? <span className="interactive-node-chip entry"><CircleDot size={11} />开场</span> : null}
            <span className="interactive-node-chapter">{data.chapterTitle || "未分章"}</span>
          </div>
          <span className={`interactive-node-kind ${isEnding ? "ending" : "story"}`}>
            {isEnding ? <Flag size={12} /> : <MessageSquareText size={12} />}
            {isEnding ? data.endingTypeLabel || "结局" : "剧情"}
          </span>
        </header>

        <div className="interactive-plot-node__body">
          <h3>{data.title || "未命名剧情"}</h3>

          {zoomTier === "full" ? (
            <div className="interactive-plot-node__meta">
              <span><MapPin size={12} />{data.scene || "地点待定"}</span>
              <span><Users size={12} />{data.participantNames?.length ? data.participantNames.join("、") : "人物待定"}</span>
            </div>
          ) : null}

          {zoomTier !== "simple" && isEnding ? (
            <div className="interactive-ending-preview">
              <Flag size={13} />
              <span>故事在这里结束</span>
            </div>
          ) : null}

          {showAnswerDetails && !isEnding ? (
            <div className="interactive-question-preview">
              <div className="interactive-question-preview__title">
                <GitBranch size={14} />
                <span>{data.question || "此刻玩家要决定什么？"}</span>
              </div>
              {answers.length ? (
                <div className="interactive-question-preview__answers">
                  {answers.map((answer, index) => {
                    const linked = Boolean(answer.targetNodeId);
                    return (
                      <div className={`interactive-answer-line ${linked ? "is-linked" : "is-unlinked"}`} key={answer.id}>
                        <span className="interactive-answer-line__index">{index + 1}</span>
                        <span className={`interactive-impact-dot ${answer.impactType || "local"}`} aria-hidden="true" />
                        <span className="interactive-answer-line__text" title={answer.text || `回答 ${index + 1}`}>
                          {answer.text || `回答 ${index + 1}`}
                        </span>
                        <span className="interactive-answer-line__target" title={linked ? answer.targetNodeTitle || "已连接后续剧情" : "从右侧圆点拖到目标节点"}>
                          {linked ? <><ArrowRight size={10} />{answer.targetNodeTitle || "已连接"}</> : "拖线连接"}
                        </span>
                        <Handle
                          id={createStoryAnswerHandleId(answer.id)}
                          type="source"
                          position={Position.Right}
                          isConnectable={isConnectable}
                          className={`interactive-node-handle interactive-answer-handle ${linked ? "is-linked" : "is-unlinked"}`}
                          title={linked ? "拖到其他节点可改接这条回答" : "拖到目标剧情节点"}
                        />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <span className="interactive-empty-answers">添加回答后，可从回答右侧圆点连接下一段剧情</span>
              )}
              <div className="interactive-branch-summary">
                <span>{answers.length} 个回答</span>
                {unlinkedCount > 0 ? <span className="interactive-unlinked">{unlinkedCount} 个待连接</span> : <span className="interactive-all-linked">均已连接</span>}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default memo(StoryPlotNode);
